import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import RemoveIcon from '@mui/icons-material/Remove';
import { AlertColor } from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import { assignJobsByProject, unassignJobsByProject } from 'src/api/jobs';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import useModality from 'src/hooks/useModality';

import CommonDialog, { CommonDialogProps } from './CommonDialog';
import {
  UserCard,
  StyledAvatar,
  InfoBox,
  InfoRow,
  PrimaryActionButton,
  DangerActionButton,
  JobCounterContainer,
  CounterButton,
  CountDisplay,
} from './SharedDialogStyles';

export const ASSIGNMENT_MODE = {
  ASSIGN: 'assign',
  UNASSIGN: 'unassign',
} as const;

export type AssignmentMode =
  (typeof ASSIGNMENT_MODE)[keyof typeof ASSIGNMENT_MODE];

export interface JobAssignDialogProps extends CommonDialogProps {
  mode?: AssignmentMode;
  assignableJobCount: number;
  currentJobCount?: number;
  annotatorName?: string;
  annotatorId?: string;
  projectId: string;
  onUpdate: () => void;
}

export default function JobAssignDialog({
  mode = ASSIGNMENT_MODE.ASSIGN,
  assignableJobCount,
  currentJobCount = 0,
  annotatorName = '',
  annotatorId = '',
  projectId,
  onUpdate,
  onClose,
  open,
}: JobAssignDialogProps): JSX.Element {
  const [jobCount, setJobCount] = useState(1);
  const { openAlertSnackbar } = useAlertSnackbar();
  const modalityLabel = useModality();

  const isAssignMode = mode === ASSIGNMENT_MODE.ASSIGN;
  const maxCount = assignableJobCount;
  const cardVariant = isAssignMode ? 'primary' : 'danger';
  const avatarVariant = isAssignMode ? 'primary' : 'danger';
  const ActionButton = isAssignMode ? PrimaryActionButton : DangerActionButton;

  const handleIncrement = () => {
    if (jobCount < maxCount) {
      setJobCount(jobCount + 1);
    }
  };

  const handleDecrement = () => {
    if (jobCount > 1) {
      setJobCount(jobCount - 1);
    }
  };

  const requestAssignJobs = async () => {
    try {
      const isAssignMode = mode === ASSIGNMENT_MODE.ASSIGN;
      const resp = isAssignMode
        ? await assignJobsByProject({
            projectId,
            userId: annotatorId,
            count: jobCount,
            modality: modalityLabel,
          })
        : await unassignJobsByProject({
            projectId,
            userId: annotatorId,
            count: jobCount,
            modality: modalityLabel,
          });

      const successCount =
        (resp as { success?: string[] }).success?.length ?? 0;
      const failCount = (resp as { failure?: string[] }).failure?.length ?? 0;
      let severity: AlertColor = 'success';
      let description = isAssignMode
        ? 'Successfully assigned new jobs'
        : 'Successfully unassigned jobs';

      if (successCount === 0 && failCount <= jobCount) {
        severity = 'error';
        description = isAssignMode
          ? 'Assigning jobs was unsuccessful. Please try again!'
          : 'Unassigning jobs was unsuccessful. Please try again!';
      }
      if (successCount > 0 && successCount < jobCount) {
        severity = 'warning';
        description = isAssignMode
          ? `Successfully assigned ${successCount} new jobs (but ${failCount} failed)`
          : `Successfully unassigned ${successCount} jobs (but ${failCount} failed)`;
      }

      openAlertSnackbar({ severity, description });
    } catch (e) {
      openAlertSnackbar({
        severity: 'error',
        description:
          mode === ASSIGNMENT_MODE.ASSIGN
            ? 'Failed to assign new jobs'
            : 'Failed to unassign jobs',
      });
    }
    onUpdate();
  };

  const afterCount = isAssignMode
    ? currentJobCount + jobCount
    : currentJobCount - jobCount;

  return (
    <CommonDialog
      onClose={onClose}
      title={isAssignMode ? 'Assign Jobs' : 'Unassign Jobs'}
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isAssignMode
            ? 'Assign annotation jobs to the selected annotator'
            : 'Remove jobs from the selected annotator'}
        </Typography>

        <UserCard variant={cardVariant}>
          <StyledAvatar variant={avatarVariant}>
            <PersonIcon sx={{ fontSize: 32 }} />
          </StyledAvatar>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Annotator
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {annotatorName}
            </Typography>
          </Box>
        </UserCard>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
          Number of Jobs to {isAssignMode ? 'Assign' : 'Unassign'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isAssignMode
            ? `Available jobs: ${assignableJobCount}`
            : `Currently assigned jobs: ${currentJobCount}`}
        </Typography>

        <JobCounterContainer>
          <CounterButton
            onClick={handleDecrement}
            disabled={jobCount <= 1}
            size="small"
          >
            <RemoveIcon />
          </CounterButton>
          <CountDisplay>{jobCount}</CountDisplay>
          <CounterButton
            onClick={handleIncrement}
            disabled={jobCount >= maxCount}
            size="small"
          >
            <AddIcon />
          </CounterButton>
        </JobCounterContainer>

        <InfoBox>
          <InfoRow>
            <Typography variant="body1">Current assigned jobs:</Typography>
            <Typography variant="body1" fontWeight={600}>
              {currentJobCount}
            </Typography>
          </InfoRow>
          <InfoRow>
            <Typography variant="body1">
              After {isAssignMode ? 'assignment' : 'unassignment'}:
            </Typography>
            <Typography
              variant="body1"
              fontWeight={600}
              color={isAssignMode ? 'primary.main' : 'error.main'}
            >
              {afterCount}
            </Typography>
          </InfoRow>
        </InfoBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ px: 3 }}>
          Cancel
        </Button>
        <ActionButton
          onClick={requestAssignJobs}
          disabled={maxCount === 0}
          variant="contained"
          sx={{ px: 3 }}
        >
          {isAssignMode ? 'Assign' : 'Unassign'} {jobCount} Job
          {jobCount !== 1 ? 's' : ''}
        </ActionButton>
      </DialogActions>
    </CommonDialog>
  );
}
